import React from "react";
import {EditButton} from "../../elements/Buttons";

export default function ConfirmationPage({ data, choices, studentStatusOptions,
                                             editingAllowed,
                                             goToStep }) {
    // Helper function to render a section with title and edit button
    const renderSection = (title, stepNumber, content) => (
        <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-md font-medium text-gray-900">{title}</h3>
                {editingAllowed && <EditButton
                    onClick={() => goToStep(stepNumber)}
                    label="Bearbeiten"
                />}
            </div>
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    {content}
                </div>
            </div>
        </div>
    );

    // Helper function to render a field with label and value
    const renderField = (label, value, colSpan = "sm:col-span-3") => (
        <div className={`${colSpan} mb-4`}>
            <dt className="text-sm font-medium text-gray-500">{label}</dt>
            <dd className="mt-1 text-sm text-gray-900">{value || "-"}</dd>
        </div>
    );

    // Helper function to render a boolean field as Ja/Nein
    const renderBooleanField = (label, value, colSpan = "sm:col-span-3") => (
        renderField(label, value ? "Ja" : "Nein", colSpan)
    );

    // Get gender label
    const getGenderLabel = (genderValue) => {
        const gender = choices?.genders?.find(g => g.value === genderValue);
        return gender ? gender.label : genderValue;
    };

    // Get diet label
    const getDietLabel = (dietValue) => {
        const diet = choices?.diet?.find(d => d.value === dietValue);
        return diet ? diet.label : dietValue;
    };

    // Get insurance type label
    const getInsuranceTypeLabel = (insuranceTypeValue) => {
        const insuranceType = choices?.insurance_type?.find(i => i.value === insuranceTypeValue);
        return insuranceType ? insuranceType.label : insuranceTypeValue;
    };

    // Get insurance via parents label
    const getInsuranceViaParentsLabel = (insuranceViaParentsValue) => {
        const insuranceViaParents = choices?.insurance_via_parents?.find(i => i.value === insuranceViaParentsValue);
        return insuranceViaParents ? insuranceViaParents.label : insuranceViaParentsValue;
    };

    // Get student status label
    const getStudentStatusLabel = (statusValue) => {
        const status = studentStatusOptions.find(s => s.value === statusValue);
        return status ? status.label : statusValue;
    };

    // Get school type label
    const getSchoolTypeLabel = (typeValue) => {
        const type = choices?.school_types?.find(t => t.value === typeValue);
        return type ? type.label : typeValue;
    };

    // Get moved to next grade label
    const getMovedToNextGradeLabel = (movedValue) => {
        const moved = choices?.moving_status?.find(m => m.value === movedValue);
        return moved ? moved.label : movedValue;
    };

    // Get extra examination label
    const getExtraExaminationLabel = (examValue) => {
        const exam = choices?.extra_examination?.find(e => e.value === examValue);
        return exam ? exam.label : examValue;
    };

    // Render student information section
    const renderStudentSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField("Vorname", data.first_name)}
            {renderField("Nachname", data.last_name)}
            {renderField("Geburtsdatum", data.birth_date ? new Date(data.birth_date).toLocaleDateString('de-DE') : "-")}
            {renderField("Geschlecht", getGenderLabel(data.gender))}
            {renderField("Klasse am 31.05.", data.grade ? `${data.grade}. Klasse` : "-", "sm:col-span-6")}
            {renderField("Handynummer Schüler", data.mobile)}
            {renderField("E-Mail Schüler", data.email)}
            {renderField("T-Shirt-Größe", data.tshirt_size)}
            {renderField("Körpergröße", data.height)}
            {renderField("Foto", data.photo ? data.photo : "Kein Foto hochgeladen", "sm:col-span-6")}
            {renderField("Status", getStudentStatusLabel(data.student_status))}
        </div>
    );

    // Render parents information section
    const renderParentsSection = () => (
        <div>
            {data.parents && data.parents.map((parent, index) => (
                <div key={parent.id || index} className="mb-6 pb-6 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                    <h4 className="text-md font-medium text-gray-900 mb-4">
                        {parent.appellation} {parent.first_name} {parent.last_name}
                    </h4>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
                        {renderField("Rolle", parent.role, "sm:col-span-6")}
                        {renderField("E-Mail", parent.email)}
                        {renderField("Handynummer", parent.mobile)}
                        {renderField("Straße", parent.street, "sm:col-span-6")}
                        {renderField("PLZ", parent.zip_code, "sm:col-span-2")}
                        {renderField("Ort", parent.city, "sm:col-span-2")}
                        {renderField("Land", parent.country, "sm:col-span-2")}
                    </div>
                </div>
            ))}
        </div>
    );

    // Render school information section
    const renderSchoolSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField("Name der Schule", data.school_name, "sm:col-span-6")}
            {renderField("Bundesland", data.school_state)}
            {renderField("Art der Schule", getSchoolTypeLabel(data.school_type))}
            {data.school_type === 'OTHER' && renderField("Schulform Details", data.school_type_remarks, "sm:col-span-6")}
            {renderField(`Ist ${data.first_name} versetzt?`, getMovedToNextGradeLabel(data.moved_to_next_grade))}
            {renderField(`Macht ${data.first_name} eine Nachprüfung?`, getExtraExaminationLabel(data.extra_examination))}
            {renderBooleanField(`Benutzt ${data.first_name} im Unterricht ein iPad?`, data.uses_ipad_in_school)}

            <div className="sm:col-span-6 mt-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Gewünschtes Fach</h4>
            </div>

            {renderField("Kursfach", data.requested_subject?.name, "sm:col-span-6")}
            {renderField("Note im letzten Zeugnis", data.subject_grade)}
            {data.requested_subject?.is_foreign_language &&
                renderField("Beginn des Fremdsprachenunterrichts",
                    data.started_foreign_language_in_grade ? `${data.started_foreign_language_in_grade}. Klasse` : "-")}
            {data.requested_subject?.name === 'Deutsch als Fremdsprache' &&
                renderField("Sprachniveau", data.gafl_level)}
            {renderField("Schwierigkeiten im Kursfach", data.subject_remarks, "sm:col-span-6")}
            {renderField("Schulbuch", data.subject_book, "sm:col-span-6")}

            <div className="sm:col-span-6 mt-4">
                <h4 className="text-md font-medium text-gray-900 mb-2">Alternatives Fach</h4>
            </div>

            {renderField("Alternatives Fach gewünscht", data.request_alternative_subject ? "Ja" : "Nein", "sm:col-span-6")}

            {data.request_alternative_subject && (
                <>
                    {renderField("Alternatives Kursfach", data.alternative_subject?.name, "sm:col-span-6")}
                    {renderField("Note im letzten Zeugnis", data.alternative_subject_grade)}
                    {data.alternative_subject?.is_foreign_language &&
                        renderField("Beginn des Fremdsprachenunterrichts",
                            data.started_foreign_language_in_grade ? `${data.started_foreign_language_in_grade}. Klasse` : "-")}
                    {data.alternative_subject?.name === 'Deutsch als Fremdsprache' &&
                        renderField("Sprachniveau", data.galternative_afl_level)}
                    {renderField("Schwierigkeiten im alternativen Kursfach", data.alternative_subject_remarks, "sm:col-span-6")}
                    {renderField("Schulbuch", data.alternative_subject_book, "sm:col-span-6")}
                </>
            )}
        </div>
    );

    // Render extra lesson section (step 4)
    const renderExtraLessonSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderBooleanField(`Möchte ${data.first_name} eine zusätzliche Einzelstunde buchen?`, data.book_extra_lesson)}
            {data.book_extra_lesson && renderField("Einzelstunde in folgendem Fach", data.extra_lesson_subject?.name, "sm:col-span-6")}
        </div>
    );

    // Render extra curricular section (step 5)
    const renderExtraCurricularSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField("Nachmittagsprogramm", data.extra_curricular?.name, "sm:col-span-6")}
            {data.extra_curricular && (
                <>
                    {renderField("Beschreibung", data.extra_curricular.description, "sm:col-span-6")}
                    {renderField("Zusatzkosten", `+${data.extra_curricular.extra_cost}€`, "sm:col-span-3")}
                </>
            )}
        </div>
    );

    // Render health section (step 6)
    const renderHealthSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField(`Wie ernährt sich ${data.first_name}?`, getDietLabel(data.diet), "sm:col-span-6")}

            {renderBooleanField(`Hat ${data.first_name} Lebenmittelunverträglichkeiten oder -allergien?`, data.dietary_restrictions_yes_no === true, "sm:col-span-6")}
            {data.dietary_restrictions_yes_no === true &&
                renderField("Besondere Anforderungen an die Ernährung", data.dietary_restrictions, "sm:col-span-6")}

            {renderBooleanField(`Leidet ${data.first_name} an einer Allergie oder anderen (schwerwiegenden) Krankheiten?`, data.illness_yes_no === true, "sm:col-span-6")}
            {data.illness_yes_no === true &&
                renderField("Krankheiten", data.illness, "sm:col-span-6")}

            {renderBooleanField(`Hat ${data.first_name} besondere gesundheitliche Einschränkungen?`, data.health_restrictions_yes_no === true, "sm:col-span-6")}
            {data.health_restrictions_yes_no === true &&
                renderField("Gesundheitliche Einschränkungen", data.health_restrictions, "sm:col-span-6")}

            {renderBooleanField(`Nimmt ${data.first_name} regelmäßig Medikamente?`, data.medication_yes_no === true, "sm:col-span-6")}
            {data.medication_yes_no === true &&
                renderField("Medikamente", data.medication, "sm:col-span-6")}

            {renderBooleanField(`Hat ${data.first_name} eine psychische Erkrankung, die im Rahmen einer stationären oder ambulanten Therapie behandelt wird?`, data.psychological_treatment === true, "sm:col-span-6")}
            {data.psychological_treatment === true &&
                renderField("Beschreibung der Erkrankung", data.psychological_treatment_remarks, "sm:col-span-6")}

            {renderField(`${data.first_name} ist wie folgt krankenversichert:`, getInsuranceTypeLabel(data.insurance_type), )}
            {renderField("Name der Krankenkasse", data.insurance)}
            {renderField("Mitversichert mit Elternteil", getInsuranceViaParentsLabel(data.insurance_via_parents))}

            {renderBooleanField(`Einverständnis Arztbesuch`, data.permission_to_see_doctor, "sm:col-span-6")}
        </div>
    );

    // Render pocket money section (step 7)
    const renderPocketMoneySection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField("Taschengeld", data.pocket_money ? `${data.pocket_money} €` : "-", "sm:col-span-6")}
        </div>
    );

    // Render permissions section (step 8)
    const renderPermissionsSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField(`Bewegungsfreiheit in Bammental`,
                data.leave_premises_permission ?
                    `Nein, ich möchte, dass ${data.first_name} das Internat nur in Begleitung eines Erwachsenen verlässt.` :
                    `Ja, ich bin einverstanden, dass ${data.first_name} sich während der Summer School in der Gemeinde Bammental frei bewegen darf.`,
                "sm:col-span-6")}

            {renderField(`Badeerlaubnis`,
                data.swimming_permission ?
                    `${data.first_name} kann nicht sicher schwimmen und darf nicht unbegleitet ins Schwimmbad gehen.` :
                    `${data.first_name} kann schwimmen und darf auf eigene Verantwortung das nahegelegene Waldschwimmbad in Bammental besuchen.`,
                "sm:col-span-6")}
        </div>
    );

    // Render how did you hear about us section (step 9)
    const renderHowDidYouHearSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderField(`Hat ${data.first_name} die Summer School schon einmal besucht?`,
                data.attended_summer_school_before ? "Ja" : "Nein",
                "sm:col-span-6")}

            {data.attended_summer_school_before &&
                renderField("In welchem Jahr?", data.attended_year, "sm:col-span-6")}

            {!data.attended_summer_school_before &&
                renderField("Wie wurden Sie auf die Summer School aufmerksam?",
                    choices?.how_did_you_hear?.find(h => h.value === data.how_did_you_hear)?.label || data.how_did_you_hear,
                    "sm:col-span-6")}

            {!data.attended_summer_school_before && data.how_did_you_hear === 'Empfehlung' &&
                renderField("Wer hat Ihnen die Summer School empfohlen?", data.recommended_by, "sm:col-span-6")}
        </div>
    );

    // Render terms and conditions section (step 10)
    const renderTermsSection = () => (
        <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-6">
            {renderBooleanField("Ich habe die Vertragsbedingungen zur Kenntnis genommen und erkenne sie an", data.contract_terms, "sm:col-span-6")}
            {renderBooleanField("Ich nehme zur Kenntnis, dass ich selbständig eine Reiserücktrittsversicherung abschließen muss, da keine Kostenerstattung bei Nichtteilnahme erfolgt.", data.travel_insurance, "sm:col-span-6")}
            {renderBooleanField("Ich nehme zur Kenntnis, dass die Anmeldung erst rechtsverbindlich wird, wenn das Kurpfalz Internat die Anmeldung bestätigt.", data.application_terms, "sm:col-span-6")}
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                <div className="md:grid md:grid-cols-3 md:gap-6">
                   <div className="mt-5 md:mt-0 md:col-span-6">
                        {renderSection("Schülerdaten", 1, renderStudentSection())}
                        {renderSection("Anmelder/Erziehungsberechtigte", 2, renderParentsSection())}
                        {renderSection("Informationen zur Schule & gewünschtes Kursfach", 3, renderSchoolSection())}
                        {(data.student_status === 'KPI' || data.student_status === 'NEU') &&
                            renderSection("Zusätzliche Einzelstunde", 4, renderExtraLessonSection())}
                        {renderSection("Freizeitmodul", 5, renderExtraCurricularSection())}
                        {renderSection("Gesundheit und Versicherung", 6, renderHealthSection())}
                        {renderSection("Taschengeld", 7, renderPocketMoneySection())}
                        {renderSection("Einverständniserklärung", 8, renderPermissionsSection())}
                        {renderSection("Wie wurden Sie auf uns aufmerksam?", 9, renderHowDidYouHearSection())}
                        {renderSection("Vertragsbedingungen", 10, renderTermsSection())}

                    </div>
                </div>
            </div>
        </div>
    );
}
